import React, { useContext, useState } from 'react';
import PropTypes from 'prop-types';
import useForm from '@afosto/utils-react/validation/useForm';
import useDataGrid from '@afosto/utils-react/datagrid/useDataGrid';
import useIntl from '@afosto/utils-react/intl/useIntl';
import { FormattedMessage } from 'react-intl';
import BatchFormContext from '../BatchFormContext';
import Form from '../../Form';
import Select from '../../Select';
import Box from '@material-ui/core/Box';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import LoadingWrapper from '@afosto/components-react/LoadingWrapper';
import camelize from '@afosto/utils/string/camelize';
import { dataset as datasetApi } from '@afosto/content/api/Dataset';
import { batches as batchesApi } from '@afosto/content/api/Batches';
import styled from 'styled-components';
import translations from './translations';

const ErrorMessages = styled(Box)`
  && {
    color: ${props => props.theme.palette.error.main};
  }
`;

const BatchMappingForm = ({ batch, onSubmit, onCancel, renderActions }) => {
  const intl = useIntl();
  const [columns, setColumns] = useState([]);
  const { action, batchReference, config, dataset } = useContext(BatchFormContext);
  const [mappingError, setMappingError] = useState(false);

  const requiredColumns = (action?.columns ?? [])
    .filter(actionColumn => actionColumn.is_required)
    .map(actionColumn => actionColumn.key);

  const fetchLines = async ({ dataProperties = {} }) => {
    try {
      const { setFixedProperties } = dataProperties;
      const response = await datasetApi.getLines(dataset?.id);
      const { data = [] } = response;

      const [firstLine] = data;
      const firstData = firstLine?.data ?? {};

      const properties = Object.keys(firstData).map(key => ({ key, name: key }));

      setFixedProperties(properties);
      setColumns(properties);

      return data
        .slice(0, 5)
        .reduce((acc, line) => [...acc, { id: line?.reference, ...(line?.data ?? {}) }], []);
    } catch (err) {
      return [];
    }
  };

  const dataSource = useDataGrid(fetchLines, {
    modules: ['dataProperties'],
  });
  const { data: rows = [] } = dataSource;

  const handleSubmit = async ({ mapping }) => {
    try {
      const formattedMapping = Object.keys(mapping).reduce(
        (acc, key) => [...acc, { from: key, to: mapping?.[key]?.length ? mapping?.[key] : '' }],
        [],
      );
      const mappedColumns = formattedMapping.map(({ to }) => to);
      const hasMissingMapping = requiredColumns.some(
        requiredColumn => !mappedColumns.includes(requiredColumn),
      );

      if (hasMissingMapping) {
        setMappingError(true);
        return Promise.reject(new Error('Not all the required columns are selected!'));
      }

      setMappingError(false);

      let batchResponse = null;

      if (batch) {
        batchResponse = await batchesApi.updateBatch(batch?.id, {
          ...batch,
          reference: batchReference ?? batch?.reference,
          import: {
            ...(batch?.import ?? {}),
            mapping: formattedMapping,
          },
        });
      } else {
        batchResponse = await batchesApi.createBatch({
          action_id: action?.id,
          reference: batchReference ?? dataset?.reference,
          dataset_id: dataset?.id,
          config,
          import: {
            mapping: formattedMapping,
          },
        });
      }

      const { data: batchData = null } = batchResponse;

      if (onSubmit && typeof onSubmit === 'function') {
        await onSubmit(batchData);
      }

      return batchData;
    } catch (err) {
      return Promise.reject(err);
    }
  };

  const existingMapping = (batch?.import?.mapping ?? []).reduce(
    (acc, mapping) => ({ ...acc, [mapping.from]: mapping.to }),
    {},
  );
  const form = useForm({
    defaultValues: {
      mapping: existingMapping,
    },
    onSubmit: handleSubmit,
    onCancel,
  });

  const getAvailableColumns = key => {
    const columnSuggestions = action?.columns.map(actionColumn => ({
      label: intl.formatMessage({
        id: `batchColumns.${camelize(actionColumn.key)}`,
        defaultMessage: actionColumn.description,
        description: `The column name for column ${actionColumn.key}`,
      }),
      value: actionColumn?.key,
    }));
    const formValues = form.getValues({ nest: true });
    const usedValues = Object.keys(formValues?.mapping ?? {})
      .filter(mappingKey => mappingKey !== key)
      .map(mappingKey => formValues?.mapping?.[mappingKey])
      .filter(value => value);

    return columnSuggestions.filter(suggestion => !usedValues.includes(suggestion.value));
  };

  const requiredColumnLabels = requiredColumns.map(requiredColumnKey =>
    intl.formatMessage({
      id: `batchColumns.${camelize(requiredColumnKey)}`,
      defaultMessage: requiredColumnKey,
      description: `The column name for column ${requiredColumnKey}`,
    }),
  );

  return (
    <LoadingWrapper isLoading={dataSource.isLoadingData || form.isSubmitting}>
      <Form form={form} renderActions={renderActions}>
        <ErrorMessages px={2}>
          {mappingError && (
            <FormattedMessage
              {...translations.mappingError}
              values={{ columns: requiredColumnLabels.join(', ') }}
            />
          )}
        </ErrorMessages>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                {columns.map(column => (
                  <TableCell
                    key={column.key}
                    style={{ background: 'none', border: 'none', paddingBottom: 4 }}
                  >
                    {column.name}
                  </TableCell>
                ))}
              </TableRow>
              <TableRow>
                {columns.map(column => (
                  <TableCell
                    key={`select-${column.key}`}
                    style={{
                      background: 'none',
                      border: 'none',
                      fontWeight: 'normal',
                      paddingBottom: 0,
                      paddingTop: 0,
                    }}
                  >
                    <Select
                      name={`mapping.${column?.key}`}
                      suggestions={getAvailableColumns(column?.key)}
                      size="small"
                      margin="dense"
                      clearable
                    />
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map(row => (
                <TableRow key={row.id}>
                  {columns.map(column => (
                    <TableCell key={`row-${row.id}-${column.key}`}>
                      {row?.[column.key] ?? '-'}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Form>
    </LoadingWrapper>
  );
};

BatchMappingForm.propTypes = {
  batch: PropTypes.object,
  onCancel: PropTypes.func,
  onSubmit: PropTypes.func,
  renderActions: PropTypes.bool,
};

BatchMappingForm.defaultProps = {
  batch: undefined,
  onCancel: undefined,
  onSubmit: undefined,
  renderActions: true,
};

export default BatchMappingForm;
